/**
 * New-template flow per ED-A12 §C3 (MVP slice): pick "Blank" or a
 * gallery starter, name it, create, land in the editor.
 *
 * Cards render name + category + block count — the spec-sanctioned
 * fallback (§E.3) until the ED-A9 pipeline ships real thumbnails.
 * Gallery failures degrade gracefully: blank creation never depends on
 * the catalog being reachable.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";

import { ApiError } from "../lib/api";
import { listGallery, type GalleryItemSummary } from "../lib/gallery";
import { getDefaultBrandKit } from "../lib/brandKits";
import { buildBlankTemplate } from "../lib/blankTemplate";
import { createTemplate, createTemplateFromGallery, generateTemplate } from "../lib/templates";
import { GalleryPreviewModal } from "./GalleryPreviewModal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const USE_CASE_LABELS: Record<string, string> = {
  welcome: "Welcome",
  newsletter: "Newsletter",
  transactional: "Transactional",
  promotional: "Promotional",
  "abandoned-cart": "Abandoned cart",
  "re-engagement": "Re-engagement",
  "event-invite": "Event",
  survey: "Survey",
  anniversary: "Anniversary",
  "feedback-request": "Feedback",
};

export function NewTemplatePanel({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}): JSX.Element {
  // null = blank canvas; otherwise the picked gallery item.
  const [picked, setPicked] = useState<GalleryItemSummary | null>(null);
  const [previewing, setPreviewing] = useState<GalleryItemSummary | null>(null);
  const [name, setName] = useState("");
  const [useCase, setUseCase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const gallery = useQuery({
    queryKey: ["template-gallery", "list"],
    queryFn: () => listGallery({ limit: 60 }),
    staleTime: 5 * 60 * 1000,
  });

  // §C3 "Apply brand kit": MVP surfaces the tenant's default kit as a
  // checkbox (on by default). 404 = no kit yet — checkbox just hides.
  const brandKit = useQuery({
    queryKey: ["brand-kit", "default"],
    queryFn: getDefaultBrandKit,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const [applyKit, setApplyKit] = useState(true);

  const items = useMemo(() => {
    const all = gallery.data?.data ?? [];
    return useCase === null
      ? all
      : all.filter((item) => item.category.use_case === useCase);
  }, [gallery.data, useCase]);

  const useCases = useMemo(() => {
    const all = gallery.data?.data ?? [];
    return [...new Set(all.map((item) => item.category.use_case))];
  }, [gallery.data]);

  const mutation = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      if (picked !== null) {
        return createTemplateFromGallery(
          name,
          picked.id,
          applyKit && brandKit.data !== undefined ? brandKit.data.id : undefined,
        );
      }
      return createTemplate(buildBlankTemplate({ name }));
    },
    onSuccess: (created) => onCreated(created.id),
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.problem.detail || err.problem.title
          : "Couldn't create template.",
      );
    },
  });

  function pick(item: GalleryItemSummary | null): void {
    setPicked(item);
    setError(null);
    // Prefill from the starter, but never clobber a name the user typed.
    if (item !== null && name.trim() === "") {
      setName(item.name);
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <section className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">New template</h2>
          <p className="text-sm text-neutral-600">
            Start from a template or a blank canvas.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </header>

      <AiGenerateBox onCreated={onCreated} />

      <div className="flex items-center gap-3 pt-1">
        <span className="h-px flex-1 bg-neutral-200" />
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
          Or start from a template
        </span>
        <span className="h-px flex-1 bg-neutral-200" />
      </div>

      {useCases.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by use case">
          <FilterPill label="All" active={useCase === null} onClick={() => setUseCase(null)} />
          {useCases.map((uc) => (
            <FilterPill
              key={uc}
              label={USE_CASE_LABELS[uc] ?? uc}
              active={useCase === uc}
              onClick={() => setUseCase(uc)}
            />
          ))}
        </div>
      )}

      <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        <li>
          <TemplateCard
            title="Blank template"
            subtitle="Start from scratch"
            selected={picked === null}
            onClick={() => pick(null)}
          />
        </li>
        {gallery.isLoading && (
          <li className="col-span-full text-sm text-neutral-500">Loading gallery…</li>
        )}
        {gallery.error !== null && !gallery.isLoading && (
          <li className="col-span-full text-sm text-neutral-500">
            Gallery is unavailable right now — you can still start blank.
          </li>
        )}
        {items.map((item) => (
          <li key={item.id}>
            <TemplateCard
              title={item.name}
              subtitle={`${USE_CASE_LABELS[item.category.use_case] ?? item.category.use_case} · ${item.metadata.block_count} blocks`}
              thumbnailUrl={item.metadata.thumbnail_url}
              selected={picked?.id === item.id}
              onClick={() => pick(item)}
              onPreview={() => setPreviewing(item)}
            />
          </li>
        ))}
      </ul>

      {previewing !== null && (
        <GalleryPreviewModal
          item={previewing}
          onUse={() => {
            pick(previewing);
            setPreviewing(null);
          }}
          onClose={() => setPreviewing(null)}
        />
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          hint="You can rename it from the editor."
        />
        {picked !== null && brandKit.data !== undefined && (
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={applyKit}
              onChange={(e) => setApplyKit(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300"
            />
            Apply my brand kit ({brandKit.data.name}) — colors &amp; fonts
          </label>
        )}
        {error !== null && (
          <p
            role="alert"
            className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          {picked === null ? "Create blank + open editor" : `Use "${picked.name}"`}
        </Button>
      </form>
    </section>
  );
}

/**
 * "✨ Generate with AI" — the headline create path: a natural-language brief
 * → ai-service blueprint mode → deterministic compiler → a premium template.
 * Self-contained (own state + mutation) so it sits atop the existing pick/blank
 * flow without disturbing it.
 */
function AiGenerateBox({ onCreated }: { onCreated: (id: string) => void }): JSX.Element {
  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const brandKit = useQuery({
    queryKey: ["brand-kit", "default"],
    queryFn: getDefaultBrandKit,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const [applyKit, setApplyKit] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      generateTemplate(
        prompt.trim(),
        name.trim() === "" ? undefined : name.trim(),
        applyKit && brandKit.data !== undefined ? brandKit.data.id : undefined,
      ),
    onSuccess: (created) => onCreated(created.id),
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? err.problem.detail || err.problem.title
          : "Couldn't generate the template. Try again.",
      ),
  });

  return (
    <section className="space-y-3 rounded-lg border border-primary-200 bg-primary-50/40 p-4">
      <div>
        <h3 className="text-sm font-semibold text-neutral-900">✨ Generate with AI</h3>
        <p className="text-xs text-neutral-600">
          Describe the email — AI composes a complete, premium, on-brand template from
          curated section patterns. You can edit everything afterwards.
        </p>
      </div>
      <textarea
        aria-label="Describe the email you want"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        maxLength={4000}
        placeholder="e.g. A summer sale for a linen clothing brand called Aurora — hero, three product cards with prices, a testimonial, and a free-shipping CTA. Warm, premium tone."
        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600"
      />
      <Input
        label="Template name (optional)"
        maxLength={120}
        value={name}
        onChange={(e) => setName(e.target.value)}
        hint="Leave blank to let AI name it."
      />
      {brandKit.data !== undefined && (
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={applyKit}
            onChange={(e) => setApplyKit(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300"
          />
          Use my brand kit ({brandKit.data.name}) — on-brand copy
        </label>
      )}
      {error !== null && (
        <p
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
        >
          {error}
        </p>
      )}
      <Button
        type="button"
        variant="primary"
        loading={mutation.isPending}
        disabled={prompt.trim() === ""}
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
      >
        Generate template
      </Button>
    </section>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? "rounded-full bg-neutral-900 px-3 py-1 text-xs font-medium text-white"
          : "rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs font-medium text-neutral-700 hover:border-neutral-400"
      }
    >
      {label}
    </button>
  );
}

function TemplateCard({
  title,
  subtitle,
  thumbnailUrl,
  selected,
  onClick,
  onPreview,
}: {
  title: string;
  subtitle: string;
  /** Rendered-template screenshot; null/load-error → text-only card (§E.3). */
  thumbnailUrl?: string | null;
  selected: boolean;
  onClick: () => void;
  /** Gallery items get a Preview affordance; the Blank card doesn't. */
  onPreview?: () => void;
}): JSX.Element {
  const [thumbBroken, setThumbBroken] = useState(false);
  const showThumb = thumbnailUrl != null && thumbnailUrl !== "" && !thumbBroken;

  return (
    <div
      className={
        "relative flex h-full flex-col overflow-hidden rounded-lg border transition-colors " +
        (selected
          ? "border-primary-600 bg-primary-50 ring-1 ring-primary-600"
          : "border-neutral-200 bg-white hover:border-neutral-400")
      }
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={onClick}
        className="flex h-full w-full flex-col items-stretch text-left"
      >
        {showThumb && (
          <img
            src={thumbnailUrl}
            alt=""
            loading="lazy"
            onError={() => setThumbBroken(true)}
            className="aspect-[4/3] w-full border-b border-neutral-100 object-cover object-top"
          />
        )}
        <span className="flex flex-col gap-1 p-3">
          <span className="text-sm font-medium text-neutral-900">{title}</span>
          <span className="text-xs text-neutral-500">{subtitle}</span>
        </span>
      </button>
      {onPreview !== undefined && (
        <button
          type="button"
          onClick={onPreview}
          aria-label={`Preview ${title}`}
          className="absolute bottom-2 right-2 rounded border border-neutral-300 bg-white px-2 py-0.5 text-xs text-neutral-600 hover:border-neutral-500 hover:text-neutral-900"
        >
          Preview
        </button>
      )}
    </div>
  );
}
